import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Client,
  type Conversation,
  type Message,
  type Participant,
  type User,
} from '@twilio/conversations'
import type {
  MessageDeliveryStatus,
  MessageType,
  ParticipantPresence,
  TwilioTokenResponse,
  UseTwilioChatOptions,
  UseTwilioChatReturn,
} from '../types/chat.types'

const isBrowser = typeof window !== 'undefined'
const DEFAULT_REFRESH_MS = 1000 * 60 * 20
const TYPING_PING_MS = 3500
const CONVERSATION_LOOKUP_RETRIES = 3
const CONVERSATION_LOOKUP_RETRY_MS = 500
const PRESENCE_POLL_MS = 1000 * 20

const sortMessages = (items: MessageType[]) =>
  [...items].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

const getClientIdentity = (twilioClient: Client) => {
  const user =
    (twilioClient as unknown as { user?: { identity?: string } }).user ?? null
  return user?.identity ?? null
}

const isTwilioConversationSid = (
  value: string | null | undefined
): value is string => Boolean(value && value.startsWith('CH'))

const resolveTwilioConversationSid = (
  ...values: Array<string | null | undefined>
): string => {
  const twilioSid = values.find(isTwilioConversationSid)
  if (twilioSid) return twilioSid
  return values.find(value => Boolean(value)) ?? ''
}

const getReceiptStatus = (message: Message): MessageDeliveryStatus => {
  const receipt = (
    message as unknown as { aggregatedDeliveryReceipt?: unknown }
  ).aggregatedDeliveryReceipt as
    | {
        sent?: number
        delivered?: number
        read?: number
        undelivered?: number
        failed?: number
      }
    | undefined

  if (!receipt) return 'sent'
  if ((receipt.read ?? 0) > 0) return 'read'
  if ((receipt.delivered ?? 0) > 0) return 'delivered'
  if ((receipt.failed ?? 0) > 0 || (receipt.undelivered ?? 0) > 0)
    return 'failed'
  return 'sent'
}

const getPresenceFromUser = (user: User | null): ParticipantPresence => {
  if (!user) return 'offline'
  const candidate = user as unknown as {
    isOnline?: boolean
    isNotifiable?: boolean
  }
  if (typeof candidate.isOnline === 'boolean') {
    return candidate.isOnline ? 'online' : 'offline'
  }
  if (typeof candidate.isNotifiable === 'boolean') {
    return candidate.isNotifiable ? 'away' : 'offline'
  }
  // Twilio reachability can be disabled for a service.
  // In that case online/offline data is unavailable.
  return 'away'
}

const isSameIdentity = (
  lhs: string | null | undefined,
  rhs: string | null | undefined
) => Boolean(lhs && rhs && lhs === rhs)

const canSubscribeUser = (
  user: User
): user is User & { subscribe: () => Promise<void> } =>
  typeof (user as unknown as { subscribe?: unknown }).subscribe === 'function'

const getReachabilityFlags = (user: User | null) => {
  if (!user) return null
  const candidate = user as unknown as {
    isOnline?: boolean
    isNotifiable?: boolean
  }
  return {
    isOnline: candidate.isOnline,
    isNotifiable: candidate.isNotifiable,
  }
}

const removeOptimisticMatch = (
  items: MessageType[],
  incoming: MessageType
): MessageType[] => {
  if (incoming.direction !== 'outbound') return items
  const index = items.findIndex(
    item =>
      item.sid.startsWith('temp-') &&
      item.direction === 'outbound' &&
      item.body === incoming.body
  )
  if (index === -1) return items
  return [...items.slice(0, index), ...items.slice(index + 1)]
}

const canSetAllMessagesRead = (
  conversation: Conversation
): conversation is Conversation & { setAllMessagesRead: () => Promise<number> } =>
  typeof (conversation as unknown as { setAllMessagesRead?: unknown })
    .setAllMessagesRead === 'function'

export const useTwilioChat = (
  options?: UseTwilioChatOptions
): UseTwilioChatReturn => {
  const [client, setClient] = useState<Client | null>(null)
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<MessageType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [typingParticipant, setTypingParticipant] = useState<string | null>(
    null
  )
  const [participantPresence, setParticipantPresence] =
    useState<ParticipantPresence>('offline')
  const [connectionState, setConnectionState] = useState('connecting')
  const [userIdentity, setUserIdentity] = useState<string | null>(
    options?.identity ?? null
  )
  const [retryNonce, setRetryNonce] = useState(0)

  const clientRef = useRef<Client | null>(null)
  const conversationRef = useRef<Conversation | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const typingTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const remoteIdentityRef = useRef<string | null>(null)
  const userIdentityRef = useRef<string | null>(options?.identity ?? null)
  const tokenFetcherRef = useRef<UseTwilioChatOptions['tokenFetcher']>(
    options?.tokenFetcher
  )
  const conversationResolverRef = useRef<
    UseTwilioChatOptions['conversationResolver']
  >(options?.conversationResolver)
  const fetchOptionsRef = useRef<UseTwilioChatOptions['fetchOptions']>(
    options?.fetchOptions
  )

  const tokenEndpoint =
    options?.tokenEndpoint ?? import.meta.env.VITE_TWILIO_TOKEN_ENDPOINT
  const autoRefreshMs = options?.autoRefreshMs ?? DEFAULT_REFRESH_MS

  useEffect(() => {
    tokenFetcherRef.current = options?.tokenFetcher
  }, [options?.tokenFetcher])

  useEffect(() => {
    conversationResolverRef.current = options?.conversationResolver
  }, [options?.conversationResolver])

  useEffect(() => {
    fetchOptionsRef.current = options?.fetchOptions
  }, [options?.fetchOptions])

  useEffect(() => {
    if (options?.identity && options.identity !== userIdentity) {
      userIdentityRef.current = options.identity
      setUserIdentity(options.identity)
    }
  }, [options?.identity, userIdentity])

  const debug = useCallback(
    (...args: unknown[]) => {
      if (options?.enableDebugLogging) {
        // eslint-disable-next-line no-console
        console.debug('[useTwilioChat]', ...args)
      }
    },
    [options?.enableDebugLogging]
  )

  const mapMessage = useCallback(
    (message: Message, identityOverride?: string | null): MessageType => {
      const author = message.author ?? 'Unknown'
      const createdAt =
        message.dateCreated?.toISOString() ?? new Date().toISOString()
      const resolvedIdentity =
        identityOverride ?? userIdentityRef.current ?? undefined
      const direction =
        resolvedIdentity && author === resolvedIdentity ? 'outbound' : 'inbound'

      return {
        sid: message.sid,
        author,
        body: message.body ?? '',
        createdAt,
        participantSid: message.participantSid ?? undefined,
        direction,
        status:
          direction === 'outbound' ? getReceiptStatus(message) : undefined,
      }
    },
    []
  )

  const clearTypingPing = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current)
      typingTimerRef.current = undefined
    }
  }, [])

  const fetchAccessToken = useCallback(
    async (signal?: AbortSignal): Promise<TwilioTokenResponse> => {
      if (tokenFetcherRef.current) {
        return tokenFetcherRef.current(signal)
      }
      if (!tokenEndpoint) {
        throw new Error(
          'Missing Twilio token endpoint. Provide it via VITE_TWILIO_TOKEN_ENDPOINT or useTwilioChat options.'
        )
      }

      const fetchOptions = fetchOptionsRef.current ?? {}
      const response = await fetch(tokenEndpoint, {
        method: 'GET',
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          ...(fetchOptions.headers ?? {}),
        },
        signal,
      })

      if (!response.ok) {
        throw new Error(
          `Unable to fetch Twilio token (${response.status} ${response.statusText})`
        )
      }

      const payload = (await response.json()) as TwilioTokenResponse
      if (!payload.token) {
        throw new Error('Twilio token response is missing token')
      }
      return payload
    },
    [tokenEndpoint]
  )

  const refreshToken = useCallback(async () => {
    try {
      const { token, identity } = await fetchAccessToken()
      if (identity && identity !== userIdentityRef.current) {
        userIdentityRef.current = identity
        setUserIdentity(identity)
      }
      if (clientRef.current) {
        await clientRef.current.updateToken(token)
      }
    } catch (refreshError) {
      debug('Token refresh failed', refreshError)
      setError('Unable to refresh Twilio token')
    }
  }, [debug, fetchAccessToken])

  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }
    if (!autoRefreshMs) return
    refreshTimerRef.current = setTimeout(() => {
      void refreshToken()
    }, autoRefreshMs)
  }, [autoRefreshMs, refreshToken])

  const refreshRemotePresence = useCallback(
    async (
      activeClient: Client,
      activeConversation: Conversation,
      identity?: string | null
    ) => {
      try {
        const participants = await activeConversation.getParticipants()
        const remoteParticipant = participants.find(
          participant =>
            participant.identity !== (identity ?? userIdentityRef.current)
        )
        const remoteIdentity = remoteParticipant?.identity ?? null
        remoteIdentityRef.current = remoteIdentity
        if (!remoteIdentity) {
          setParticipantPresence('offline')
          return
        }
        const remoteUser = await activeClient.getUser(remoteIdentity)
        if (canSubscribeUser(remoteUser)) {
          await remoteUser.subscribe().catch(() => {
            /* noop */
          })
        }
        debug(
          'Remote reachability',
          remoteIdentity,
          getReachabilityFlags(remoteUser)
        )
        setParticipantPresence(getPresenceFromUser(remoteUser))
      } catch (presenceError) {
        debug('Failed to refresh participant presence', presenceError)
      }
    },
    [debug]
  )

  const primeMessages = useCallback(
    async (activeConversation: Conversation, identity?: string | null) => {
      const paginator = await activeConversation.getMessages()
      const hydrated = paginator.items.map(item => mapMessage(item, identity))
      setMessages(sortMessages(hydrated))
    },
    [mapMessage]
  )

  const markConversationAsRead = useCallback(
    async (activeConversation: Conversation, latestMessage?: Message | null) => {
      try {
        if (canSetAllMessagesRead(activeConversation)) {
          await activeConversation.setAllMessagesRead()
          return
        }

        const conversationWithAdvance = activeConversation as unknown as {
          advanceLastReadMessageIndex?: (index: number) => Promise<number>
        }
        if (
          latestMessage &&
          typeof latestMessage.index === 'number' &&
          typeof conversationWithAdvance.advanceLastReadMessageIndex ===
            'function'
        ) {
          await conversationWithAdvance.advanceLastReadMessageIndex(
            latestMessage.index
          )
        }
      } catch (readError) {
        debug('Failed to mark conversation as read', readError)
      }
    },
    [debug]
  )

  const attachConversationListeners = useCallback(
    (
      activeConversation: Conversation,
      activeClient: Client,
      identity?: string | null
    ) => {
      const myIdentity = identity ?? userIdentityRef.current

      const handleMessageAdded = (message: Message) => {
        const isInbound = !isSameIdentity(message.author, myIdentity)
        if (isInbound) {
          setTypingParticipant(null)
          setParticipantPresence('online')
          void markConversationAsRead(activeConversation, message)
        }
        setMessages(prev => {
          const mapped = mapMessage(message, myIdentity)
          const withoutOptimistic = removeOptimisticMatch(prev, mapped)
          return sortMessages([
            ...withoutOptimistic.filter(item => item.sid !== mapped.sid),
            mapped,
          ])
        })
      }

      const handleMessageUpdated = ({ message }: { message: Message }) => {
        setMessages(prev =>
          sortMessages(
            prev.map(item =>
              item.sid === message.sid ? mapMessage(message, myIdentity) : item
            )
          )
        )
      }

      const handleTypingStarted = (participant: Participant) => {
        if (isSameIdentity(participant.identity, myIdentity)) return
        setTypingParticipant(participant.identity ?? 'Someone')
        setParticipantPresence('online')
      }

      const handleTypingEnded = (participant: Participant) => {
        if (isSameIdentity(participant.identity, myIdentity)) return
        setTypingParticipant(current =>
          current === (participant.identity ?? 'Someone') ? null : current
        )
        void refreshRemotePresence(activeClient, activeConversation, myIdentity)
      }

      const handleParticipantUpdated = () => {
        void refreshRemotePresence(activeClient, activeConversation, myIdentity)
      }

      const handleParticipantJoined = (participant: Participant) => {
        if (isSameIdentity(participant.identity, myIdentity)) return
        setParticipantPresence('online')
      }

      const handleParticipantLeft = (participant: Participant) => {
        if (isSameIdentity(participant.identity, myIdentity)) return
        setTypingParticipant(null)
        setParticipantPresence('offline')
      }

      activeConversation.on('messageAdded', handleMessageAdded)
      activeConversation.on('messageUpdated', handleMessageUpdated)
      activeConversation.on('typingStarted', handleTypingStarted)
      activeConversation.on('typingEnded', handleTypingEnded)
      activeConversation.on('participantUpdated', handleParticipantUpdated)
      activeConversation.on('participantJoined', handleParticipantJoined)
      activeConversation.on('participantLeft', handleParticipantLeft)

      return () => {
        activeConversation.off('messageAdded', handleMessageAdded)
        activeConversation.off('messageUpdated', handleMessageUpdated)
        activeConversation.off('typingStarted', handleTypingStarted)
        activeConversation.off('typingEnded', handleTypingEnded)
        activeConversation.off('participantUpdated', handleParticipantUpdated)
        activeConversation.off('participantJoined', handleParticipantJoined)
        activeConversation.off('participantLeft', handleParticipantLeft)
      }
    },
    [mapMessage, markConversationAsRead, refreshRemotePresence]
  )

  const attachClientListeners = useCallback(
    (
      activeClient: Client,
      activeConversation: Conversation,
      identity?: string | null
    ) => {
      let presencePollTimer: ReturnType<typeof setInterval> | null = null

      const handleConnectionChange = (state: string) => {
        setConnectionState(state)
        if (state === 'connected') {
          setError(null)
        } else if (state === 'disconnected' || state === 'disconnecting') {
          setError('Connection lost. Reconnecting...')
        }
      }

      const handleUserUpdated = ({ user }: { user: User }) => {
        if (
          !remoteIdentityRef.current ||
          user.identity !== remoteIdentityRef.current
        ) {
          return
        }
        setParticipantPresence(getPresenceFromUser(user))
      }

      const handleUserSubscribed = (user: User) => {
        if (
          !remoteIdentityRef.current ||
          user.identity !== remoteIdentityRef.current
        ) {
          return
        }
        setParticipantPresence(getPresenceFromUser(user))
      }

      activeClient.on('tokenAboutToExpire', refreshToken)
      activeClient.on('tokenExpired', refreshToken)
      activeClient.on('connectionStateChanged', handleConnectionChange)
      activeClient.on('userUpdated', handleUserUpdated)
      activeClient.on('userSubscribed', handleUserSubscribed)
      void refreshRemotePresence(activeClient, activeConversation, identity)
      presencePollTimer = setInterval(() => {
        void refreshRemotePresence(activeClient, activeConversation, identity)
      }, PRESENCE_POLL_MS)

      return () => {
        activeClient.off('tokenAboutToExpire', refreshToken)
        activeClient.off('tokenExpired', refreshToken)
        activeClient.off('connectionStateChanged', handleConnectionChange)
        activeClient.off('userUpdated', handleUserUpdated)
        activeClient.off('userSubscribed', handleUserSubscribed)
        if (presencePollTimer) {
          clearInterval(presencePollTimer)
        }
      }
    },
    [refreshRemotePresence, refreshToken]
  )

  const retryConnection = useCallback(() => {
    setError(null)
    setConnectionState('connecting')
    setRetryNonce(current => current + 1)
  }, [])

  useEffect(() => {
    if (!isBrowser) {
      setError('Twilio Conversations is only available in the browser.')
      setLoading(false)
      return
    }

    let isMounted = true
    let detachConversationListeners: (() => void) | null = null
    let detachClientListeners: (() => void) | null = null
    const abortController = new AbortController()

    const initialise = async () => {
      setLoading(true)
      setError(null)
      setConnectionState('connecting')
      setParticipantPresence('offline')

      try {
        let targetConversationSid = resolveTwilioConversationSid(
          options?.conversationSid,
          options?.conversationId
        )
        if (!targetConversationSid && conversationResolverRef.current) {
          targetConversationSid = await conversationResolverRef.current(
            abortController.signal
          )
        }

        if (
          targetConversationSid &&
          !isTwilioConversationSid(targetConversationSid) &&
          conversationResolverRef.current
        ) {
          targetConversationSid = await conversationResolverRef.current(
            abortController.signal
          )
        }

        const { token, conversationSid, identity } = await fetchAccessToken(
          abortController.signal
        )
        if (!isMounted || abortController.signal.aborted) return

        targetConversationSid = resolveTwilioConversationSid(
          targetConversationSid,
          conversationSid
        )

        if (!targetConversationSid) {
          throw new Error('Conversation SID is missing.')
        }
        if (!isTwilioConversationSid(targetConversationSid)) {
          throw new Error(
            `Invalid Twilio conversation SID: "${targetConversationSid}". Expected value starting with "CH".`
          )
        }

        const twilioClient = new Client(token)
        // console.log('twilioClient', twilioClient)
        if (!isMounted || abortController.signal.aborted) return

        clientRef.current = twilioClient
        setClient(twilioClient)

        const derivedIdentity =
          options?.identity ??
          identity ??
          getClientIdentity(twilioClient) ??
          userIdentityRef.current ??
          null
        if (derivedIdentity) {
          // console.log('derivedIdentity', derivedIdentity)
          userIdentityRef.current = derivedIdentity
          setUserIdentity(derivedIdentity)
        }

        let activeConversation: Conversation
        try {
          let lastError: unknown = null
          for (
            let attempt = 0;
            attempt < CONVERSATION_LOOKUP_RETRIES;
            attempt += 1
          ) {
            try {
              if (attempt > 0) {
                await new Promise(resolve =>
                  setTimeout(
                    resolve,
                    CONVERSATION_LOOKUP_RETRY_MS * Math.pow(2, attempt - 1)
                  )
                )
              }
              // console.log('targetConversationSid', targetConversationSid)
              activeConversation = await twilioClient.getConversationBySid(
                targetConversationSid
              )
              // console.log('activeConversation', activeConversation)
              lastError = null
              break
            } catch (lookupError) {
              // console.log('lookupError', lookupError)
              lastError = lookupError
            }
          }

          if (lastError) {
            throw lastError
          }
        } catch (sidLookupError) {
          const message =
            sidLookupError instanceof Error
              ? sidLookupError.message.toLowerCase()
              : String(sidLookupError).toLowerCase()
          const shouldRetryResolve =
            message.includes('not found') && conversationResolverRef.current

          if (!shouldRetryResolve) {
            throw sidLookupError
          }

          const resolvedSid = await conversationResolverRef.current(
            abortController.signal
          )
          if (!isTwilioConversationSid(resolvedSid)) {
            throw new Error(
              `Conversation resolver did not return a valid Twilio SID. Received: "${resolvedSid}".`
            )
          }
          let lastRetryError: unknown = null
          for (
            let attempt = 0;
            attempt < CONVERSATION_LOOKUP_RETRIES;
            attempt += 1
          ) {
            try {
              if (attempt > 0) {
                await new Promise(resolve =>
                  setTimeout(
                    resolve,
                    CONVERSATION_LOOKUP_RETRY_MS * Math.pow(2, attempt - 1)
                  )
                )
              }
              activeConversation =
                await twilioClient.getConversationBySid(resolvedSid)
              lastRetryError = null
              break
            } catch (retryError) {
              lastRetryError = retryError
            }
          }
          if (lastRetryError) {
            throw lastRetryError
          }
        }
        if (!isMounted || abortController.signal.aborted) return

        conversationRef.current = activeConversation
        setConversation(activeConversation)

        await primeMessages(activeConversation, derivedIdentity)
        const lastMessage = (await activeConversation.getMessages(1)).items[0] ?? null
        await markConversationAsRead(activeConversation, lastMessage)
        if (!isMounted || abortController.signal.aborted) return

        detachConversationListeners = attachConversationListeners(
          activeConversation,
          twilioClient,
          derivedIdentity
        )
        detachClientListeners = attachClientListeners(
          twilioClient,
          activeConversation,
          derivedIdentity
        )

        scheduleTokenRefresh()
        setConnectionState('connected')
      } catch (initialisationError) {
        if (!isMounted || abortController.signal.aborted) return
        debug('initialisation error', initialisationError)
        setError(
          initialisationError instanceof Error
            ? initialisationError.message
            : 'Unable to start chat'
        )
        setConnectionState('disconnected')
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    void initialise()

    return () => {
      isMounted = false
      abortController.abort()
      clearTypingPing()

      if (detachConversationListeners) {
        detachConversationListeners()
      }
      if (detachClientListeners) {
        detachClientListeners()
      }
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }

      if (conversationRef.current) {
        conversationRef.current.removeAllListeners?.()
        conversationRef.current = null
      }
      if (clientRef.current) {
        const clientInstance = clientRef.current
        clientInstance.removeAllListeners?.()
        clientInstance.shutdown().catch(() => {
          /* noop */
        })
        clientRef.current = null
      }
    }
  }, [
    attachClientListeners,
    attachConversationListeners,
    clearTypingPing,
    debug,
    fetchAccessToken,
    options?.conversationId,
    options?.conversationSid,
    options?.identity,
    primeMessages,
    markConversationAsRead,
    retryNonce,
    scheduleTokenRefresh,
  ])

  const sendMessage = useCallback(
    async (message: string) => {
      const trimmed = message.trim()
      if (!trimmed) return

      const activeConversation = conversationRef.current
      if (!activeConversation) {
        throw new Error('Conversation is not ready yet.')
      }

      const tempSid = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const optimisticMessage: MessageType = {
        sid: tempSid,
        author: userIdentityRef.current ?? userIdentity ?? 'Me',
        body: trimmed,
        createdAt: new Date().toISOString(),
        direction: 'outbound',
        status: 'sending',
      }
      setMessages(prev => sortMessages([...prev, optimisticMessage]))

      try {
        await activeConversation.sendMessage(trimmed)
      } catch (sendError) {
        setMessages(prev =>
          sortMessages(
            prev.map(item =>
              item.sid === tempSid ? { ...item, status: 'failed' } : item
            )
          )
        )
        setError('Unable to send message. Please retry.')
        throw sendError
      }
    },
    [userIdentity]
  )

  const beginTyping = useCallback(() => {
    const activeConversation = conversationRef.current
    if (!activeConversation) return
    activeConversation.typing().catch(() => {
      /* noop */
    })

    clearTypingPing()
    typingTimerRef.current = setTimeout(() => {
      activeConversation.typing().catch(() => {
        /* noop */
      })
    }, TYPING_PING_MS)
  }, [clearTypingPing])

  const endTyping = useCallback(() => {
    clearTypingPing()
  }, [clearTypingPing])

  return {
    client,
    conversation,
    messages,
    loading,
    error,
    connectionState,
    participantPresence,
    sendMessage,
    retryConnection,
    beginTyping,
    endTyping,
    typingParticipant,
    participantDisplayName: options?.participantDisplayName ?? null,
    participantAvatarUrl: options?.participantAvatarUrl ?? null,
    currentUserDisplayName: options?.currentUserDisplayName ?? null,
  }
}
